import React, {useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { Box, Typography, RadioGroup, FormControlLabel, Radio, Button, TextField, CircularProgress } from '@mui/material';
import { useAppContext } from '../../context/AppContext';
import { combinationSkinIcon, drySkinIcon, normalSkinIcon, notSureIcon, oilySkinIcon } from '../../assets';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import TransgenderIcon from '@mui/icons-material/Transgender';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckIcon from '@mui/icons-material/Check';
import ApiService from '../../services/api';
import { COMMON_MUI_STYLES } from '../../styles';

// Utility function to decode HTML entities
const decodeHtmlEntities = (text: string): string => {
    if (!text) return text;
    
    return text
        .replace(/&#x27;/g, "'")
        .replace(/&apos;/g, "'")
        .replace(/&#39;/g, "'")
        .replace(/&quot;/g, '"')
        .replace(/&#x22;/g, '"')
        .replace(/&amp;/g, '&')
        .replace(/&#x26;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&#x3C;/g, '<')
        .replace(/&#x3E;/g, '>');
};

const UserInfoPage: React.FC = () => {
    const { setCurrentView, sessionId, userId, role, getTranslation, setSnackbar, currentLanguage, translationData } = useAppContext();
    const [age, setAge] = useState('');
    const [skin, setSkin] = useState('');
    const [gender, setGender] = useState('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [privacyConsent, setPrivacyConsent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

const ageGroups = [
  { label: getTranslation("Txt669"), value: "18-24" },
  { label: getTranslation("Txt670"), value: "25-34" },
  { label: getTranslation("Txt671"), value: "35-44" },
  { label: getTranslation("Txt672"), value: "45-54" },
  { label: getTranslation("Txt673"), value: "55+" },
];

const skinTypes = [
  { label: getTranslation("Txt103"), icon: normalSkinIcon, value: "normal" },
  { label: getTranslation("Txt104"), icon: drySkinIcon, value: "dry" },
  { label: getTranslation("Txt105"), icon: oilySkinIcon, value: "oily" },
  { label: getTranslation("Txt106"), icon: combinationSkinIcon, value: "combination" },
  { label: getTranslation("Txt107"), icon: notSureIcon, value: "not_sure" },
];

const genders = [
  { label: getTranslation("Txt109"), icon: MaleIcon, value: "male" },
  { label: getTranslation("Txt110"), icon: FemaleIcon, value: "female" },
  { label: getTranslation("Txt111"), icon: TransgenderIcon, value: "other" },
  { label: getTranslation("Txt112"), icon: HelpOutlineIcon, value: "not_sure" },
];


    const [errors, setErrors] = useState({
        age: '',
        skin: '',
        gender: '',
        name: '',
        email: ''
    });

    const handleValidation = useCallback((showSnackbar: boolean = true) => {
        let valid = true;
        const newErrors: any = {
            age: '',
            skin: '',
            gender: '',
            name: '',
            email: ''
        };

        if (!age) {
            newErrors.age = getTranslation("Txt674");
            valid = false;
        }

        if (!skin) {
            newErrors.skin = getTranslation("Txt681") + " *";
            valid = false;
        }

        if (!gender) {
            newErrors.gender = getTranslation("Txt687");
            valid = false;
        }
        const trimmedName = name.trim();
        if (role === 1) {
            if (!trimmedName) {
                newErrors.name = getTranslation("Txt930");
                valid = false;
            } else if (!/^[\p{L}\s']+$/u.test(trimmedName)) {
                newErrors.name = getTranslation("Txt930");
                valid = false;
            }
        } else if (trimmedName && !/^[\p{L}\s']+$/u.test(trimmedName)) {
            newErrors.name = getTranslation("Txt930");
            valid = false;
        }

        // Email validation only for role 1 (distributor)
        if (role === 1) {
            if (email && !/\S+@\S+\.\S+/.test(email)) {
                newErrors.email = getTranslation("Txt642");
                valid = false;
            }
        }

        setErrors(newErrors);
        if (!valid && showSnackbar) {
            const message =
                newErrors.name ||
                newErrors.age ||
                newErrors.skin ||
                newErrors.gender ||
                newErrors.email 

            setSnackbar({
                open: true,
                severity: "error",
                message,
            });
        }
        return valid;
    }, [age, skin, gender, name, email, role, getTranslation, setSnackbar, currentLanguage]);

    // Re-validate form errors when language changes to update error messages
    useEffect(() => {
        // Only re-validate if there are existing errors and translations are loaded for the current language
        const hasErrors = Object.values(errors).some(error => error !== '');
        const translationsLoaded = translationData && translationData.language === currentLanguage;
        
        if (hasErrors && translationsLoaded) {
            handleValidation(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLanguage, translationData, handleValidation]);

    const handleAgeChange = (value: string) => {
        setAge(value);
        if (value) {
            setErrors((prev) => ({ ...prev, age: '' }));
        }
    };

    const handleSkinChange = (value: string) => {
        setSkin(value);
        if (value) {
            setErrors((prev) => ({ ...prev, skin: '' }));
        }
    };

    const handleGenderChange = (value: string) => {
        setGender(value);
        if (value) {
            setErrors((prev) => ({ ...prev, gender: '' }));
        }
    };

    const handleNameChange = (value: string) => {
        const decodedValue = decodeHtmlEntities(value);
        const trimmedValue = decodedValue.trim();
        setName(decodedValue);

        if (role === 1) {
            if (!trimmedValue) {
                setErrors((prev) => ({ ...prev, name: getTranslation("Txt930") }));
            } else if (!/^[\p{L}\s']+$/u.test(trimmedValue)) {
                setErrors((prev) => ({ ...prev, name: getTranslation("Txt930") }));
            } else {
                setErrors((prev) => ({ ...prev, name: '' }));
            }
        } else {
            if (trimmedValue && !/^[\p{L}\s']+$/u.test(trimmedValue)) {
                setErrors((prev) => ({ ...prev, name: getTranslation("Txt930") }));
            } else {
                setErrors((prev) => ({ ...prev, name: '' }));
            }
        }
    };

    const handleEmailChange = (value: string) => {
        setEmail(value);
        if (value) {
            setErrors((prev) => ({ ...prev, email: '' }));
        }
    };

    const onPrivacyConsentChange = (value: boolean) => {
        setPrivacyConsent(value);
    };

    const handleSubmit = async () => {
        if (handleValidation()) {
            setIsLoading(true);
            try {
                const api = new ApiService(sessionId);
                
                if (userId) {
                    await api.updateUserProfile({
                        userId: userId,
                        gender: gender,
                        age_range: age,
                        skin_type: skin
                    });
                } else {
                    const sessionData = {
                        gender: gender,
                        age_range: age,
                        skin_type: skin,
                        ...(name && { guest_name: name }),
                        ...(email && { guest_email: email.toLowerCase() }),
                        is_anonymous: true
                    };
                    
                    await api.updateUserSession(sessionData);
                }
                
                // Navigate to next page
                setCurrentView("selfie");
            } catch (error) {
                console.error("Error updating user profile/session:", error);
                // You can add error handling here (e.g., show a snackbar)
            } finally {
                setIsLoading(false);
            }
        }
    };



    return (
        <Layout>
            <Box className="user-info"
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: "20px", md: "16px" },
                    alignItems: { xs: "center", sm: "flex-start" },
                    justifyContent: { xs: "center", sm: "flex-start" },
                    padding: { xs: "16px 0px", sm: "16px" },
                    width: '100%',
                    paddingLeft: { xs: '0px', sm: '48px', md: '100px' }
                }}>
                    <Typography variant="h5" component="h1" sx={{
                        color: "var(--color-text-primary)",
                        textAlign: { xs: "center", sm: "left" },
                        fontSize: { xs: "18px", sm: "24px", md: "30px" },
                        fontStyle: "normal",
                        fontWeight: 700,
                        lineHeight: { xs: "1.4", sm: "1.3", md: "32px" },
                        mb: { xs: 2, sm: 1 }
                    }}>
                        {getTranslation("Txt23")}
                    </Typography>

                {/* Name and Email Fields - Only for Role 1 (Distributor) */}
                {role === 1 && (
                    <>
                        {/* Name Field */}
                        <Box sx={{
                            width: { xs: '100%', sm: 'auto' },
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: { xs: 'center', sm: 'flex-start' }
                        }}>
                            <Typography className='form-label' sx={{
                                textAlign: { xs: 'center', sm: 'left' },
                                mb: { xs: 2, sm: 1 }
                            }}>{getTranslation("Txt489")}</Typography>
                            <TextField
                                required
                                value={name}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    // Only allow letters and spaces (no numbers or special characters)
                                    handleNameChange(value);
                                }}
                                placeholder={getTranslation("Txt876")}
                                variant="outlined"
                                error={Boolean(errors.name)}
                                sx={{
                                    width: { xs: '100%', sm: '400px' },
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        border: errors.name ? '3px solid #FF2B1F' : '1.5px solid #000000',
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '&:hover fieldset': {
                                            border: 'none',
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: 'none',
                                        },
                                    },
                                }}
                            />
                            <Typography
                                sx={{
                                    color: '#FF2B1F',
                                    fontSize: '0.875rem',
                                    minHeight: '1.25rem',
                                    opacity: errors.name ? 1 : 0,
                                    visibility: errors.name ? 'visible' : 'hidden',
                                    transition: 'opacity 0.3s ease, visibility 0.3s ease',
                                    mt: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '50%',
                                        backgroundColor: '#FF2B1F',
                                        color: 'var(--color-text-inverse)',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        lineHeight: 'normal'
                                    }}
                                >
                                    !
                                </Box>
                                {errors.name}
                            </Typography>
                        </Box>

                        {/* Email Field */}
                        <Box sx={{
                            width: { xs: '100%', sm: 'auto' },
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: { xs: 'center', sm: 'flex-start' }
                        }}>
                            <Typography className='form-label' sx={{
                                textAlign: { xs: 'center', sm: 'left' },
                                mb: { xs: 2, sm: 1 }
                            }}>{getTranslation("Txt878")}</Typography>
                            <TextField
                                value={email}
                                onChange={(e) => handleEmailChange(e.target.value)}
                                // placeholder={getTranslation("Txt220")}
                                placeholder={getTranslation("Txt7").replace(" *", "")}
                                type="email"
                                variant="outlined"
                                sx={{
                                    width: { xs: '100%', sm: '400px' },
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        border: errors.email ? '3px solid #FF2B1F' : '1.5px solid #000000',
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '&:hover fieldset': {
                                            border: 'none',
                                        },
                                        '&.Mui-focused fieldset': {
                                            border: 'none',
                                        },
                                    },
                                }}
                            />
                            <Typography
                                sx={{
                                    color: '#FF2B1F',
                                    fontSize: '0.875rem',
                                    minHeight: '1.25rem',
                                    opacity: errors.email ? 1 : 0,
                                    visibility: errors.email ? 'visible' : 'hidden',
                                    transition: 'opacity 0.3s ease, visibility 0.3s ease',
                                    mt: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '50%',
                                        backgroundColor: '#FF2B1F',
                                        color: 'var(--color-text-inverse)',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        lineHeight: 'normal'
                                    }}
                                >
                                    !
                                </Box>
                                {errors.email}
                            </Typography>
                        </Box>
                    </>
                )}

                <Box sx={{
                    width: { xs: '100%', sm: 'auto' },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: { xs: 'center', sm: 'flex-start' }
                }}>
                    <Typography className='form-label' sx={{
                        textAlign: { xs: 'center', sm: 'left' },
                        mb: { xs: 2, sm: 1 }
                    }}>{getTranslation("Txt96")}</Typography>
                    <RadioGroup
                        row
                        value={age}
                        onChange={(e) => handleAgeChange(e.target.value)}
                        sx={{
                            display: 'flex',
                            gap: { xs: '12px', md: '20px' },
                            justifyContent: { xs: "center", sm: "flex-start" },
                            alignItems: { xs: "center", sm: "flex-start" },
                            flexWrap: 'wrap'
                        }}
                    >
                        {ageGroups.map((group) => (
                            <Box
                                key={group.value}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: { xs: "100px", md: '120px' },
                                    padding: { xs: '12px 8px', md: '15px 20px' },
                                    borderRadius: '8px',
                                    border: age === group.value ? 'none' : (errors.age ? '3px solid #FF2B1F' : '1.5px solid #000000'),
                                    backgroundColor: age === group.value ? 'var(--color-primary)' : 'var(--color-background)',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.3s, border 0.3s',
                                    flexShrink: 0,
                                }}
                                onClick={() => handleAgeChange(group.value)}
                            >
                                <FormControlLabel
                                    value={group.value}
                                    control={<Radio sx={{ display: 'none' }} />}
                                    label={group.label}
                                    sx={{
                                        color: age === group.value ? 'var(--color-text-inverse)' : 'var(--color-text-primary)',
                                        textAlign: 'center',
                                        fontSize: { xs: '14px', md: '18px' },
                                        fontWeight: age === group.value ? 700 : 400,
                                        lineHeight: '1.2',
                                        margin: 0,
                                        '& .MuiTypography-root': {
                                            fontWeight: age === group.value ? 700 : 400,
                                            fontSize: { xs: '14px', md: '18px' },
                                        },
                                    }}
                                />
                            </Box>
                        ))}
                    </RadioGroup>
                    <Typography
                        sx={{
                            color: '#FF2B1F',
                            fontSize: '0.875rem',
                            minHeight: '1.25rem',
                            opacity: errors.age ? 1 : 0,
                            visibility: errors.age ? 'visible' : 'hidden',
                            transition: 'opacity 0.3s ease, visibility 0.3s ease',
                            mt: '4px',
                             display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                        }}
                        >
                          <Box
                                component="span"
                                sx={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                backgroundColor: '#FF2B1F',
                                color: 'var(--color-text-inverse)',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                     lineHeight:'normal'
                                }}
                            >
                                !
                            </Box>
                    {errors.age}
                        </Typography>
                </Box>

                <Box sx={{
                    width: { xs: '100%', sm: 'auto' },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: { xs: 'center', sm: 'flex-start' }
                }}>
                    <Typography className='form-label' sx={{
                        textAlign: { xs: 'center', sm: 'left' },
                        mb: { xs: 2, sm: 1 }
                    }}>{getTranslation("Txt102")}</Typography>
                    <RadioGroup
                        row
                        value={skin}
                        onChange={(e) => handleSkinChange(e.target.value)}
                        sx={{
                            display: 'flex',
                            gap: { xs: '12px', md: '20px' },
                            justifyContent: { xs: "center", sm: "flex-start" },
                            alignItems: { xs: "center", sm: "flex-start" },
                            flexWrap: 'wrap'
                        }}
                    >
                    {skinTypes.map(({ label, icon, value }) => (
                            <Box
                                key={value}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    justifyContent: { xs: "center", sm: 'flex-start' },
                                    minWidth: { xs: "120px", md: '200px' },
                                    padding: { xs: '12px 8px', md: '15px 20px' },
                                    borderRadius: '8px',
                                    border: skin === value ? 'none' : (errors.skin ? '3px solid #FF2B1F' : '1.5px solid #000000'),
                                    backgroundColor: skin === value ? 'var(--color-primary)' : 'var(--color-background)',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.3s, border 0.3s',
                                    flexShrink: 0,
                                    gap: { xs: '8px', md: "15px" }
                                }}
                                onClick={() => handleSkinChange(value)}
                            >
                                <Box
                                    sx={{
                                        width: { xs: '20px', md: '36px' },
                                        height: { xs: '20px', md: '36px' },
                                        backgroundImage: `url(${icon})`,
                                        backgroundSize: 'contain',
                                        backgroundRepeat: "no-repeat",
                                        backgroundPosition: 'center',
                                        filter: skin === value ? 'invert(100%)' : 'none',
                                        flexShrink: 0
                                    }}
                                />
                                <FormControlLabel
                                    value={value}
                                    control={<Radio sx={{ display: 'none' }} />}
                                     label={label}
                                    sx={{
                                        color: skin === value ? 'var(--color-text-inverse)' : 'var(--color-text-primary)',
                                        textAlign: 'center',
                                        fontSize: { xs: '14px', md: '18px' },
                                        fontWeight: skin === value ? 700 : 400,
                                        lineHeight: '1.2',
                                        margin: 0,
                                        // textTransform: skin === value ? 'capitalize' : 'none',
                                        '& .MuiTypography-root': {
                                            fontWeight: skin === value ? 700 : 400,
                                            fontSize: { xs: '14px', md: '18px' },
                                        },
                                    }}
                                />
                            </Box>
                        ))}
                    </RadioGroup>
                    <Typography
                        sx={{
                            color: '#FF2B1F',
                            fontSize: '0.875rem',
                            minHeight: '1.25rem',
                            opacity: errors.skin ? 1 : 0,
                            visibility: errors.skin ? 'visible' : 'hidden',
                            transition: 'opacity 0.3s ease, visibility 0.3s ease',
                            mt: '4px',
                           display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                        }}
                        >
                            <Box
                                component="span"
                                sx={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                backgroundColor: '#FF2B1F',
                                color: 'var(--color-text-inverse)',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                     lineHeight:'normal'
                                }}
                            >
                                !
                            </Box>
                        {errors.skin}
                        </Typography>
                </Box>

                <Box sx={{
                    width: { xs: '100%', sm: 'auto' },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: { xs: 'center', sm: 'flex-start' }
                }}>
                    <Typography className='form-label' sx={{
                        textAlign: { xs: 'center', sm: 'left' },
                        mb: { xs: 2, sm: 1 }
                    }}>{getTranslation("Txt108")}</Typography>
                    <RadioGroup
                        row
                        value={gender}
                        onChange={(e) => handleGenderChange(e.target.value)}
                        sx={{
                            display: 'flex',
                            gap: { xs: '12px', md: '20px' },
                            justifyContent: { xs: "center", sm: "flex-start" },
                            alignItems: { xs: "center", sm: "flex-start" },
                            flexWrap: 'wrap'
                        }}
                    >
                  {genders.map(({ label, icon: IconComponent, value }) => (
                            <Box
                                key={value}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    justifyContent: { xs: "center", sm: 'flex-start' },
                                    minWidth: { xs: "120px", md: '200px' },
                                    padding: { xs: '12px 8px', md: '15px 20px' },
                                    borderRadius: '8px',
                                    border: gender === value ? 'none' : (errors.gender ? '3px solid #FF2B1F' : '1.5px solid #000000'),
                                    backgroundColor: gender === value ? 'var(--color-primary)' : 'var(--color-background)',
                                    cursor: 'pointer',
                                    transition: 'background-color 0.3s, border 0.3s',
                                    flexShrink: 0,
                                    gap: { xs: '8px', md: "15px" }
                                }}
                                onClick={() => handleGenderChange(value)}
                            >
                                <IconComponent
                                    sx={{
                                        width: { xs: '20px', md: '34px' },
                                        height: { xs: '20px', md: '34px' },
                                        color: gender === value ? 'var(--color-text-inverse)' : 'var(--color-text-primary)',
                                        flexShrink: 0
                                    }}
                                />
                                <FormControlLabel
                                    value={value}
                                    control={<Radio sx={{ display: 'none' }} />}
                                  label={label}
                                    sx={{
                                        color: gender === value ? 'var(--color-text-inverse)' : 'var(--color-text-primary)',
                                        textAlign: 'center',
                                        fontSize: { xs: '14px', md: '18px' },
                                        fontWeight: gender === value ? 700 : 400,
                                        lineHeight: '1.2',
                                        margin: 0,
                                        // textTransform: gender === value ? 'capitalize' : 'none',
                                        '& .MuiTypography-root': {
                                            fontWeight: gender === value ? 700 : 400,
                                            fontSize: { xs: '14px', md: '18px' },
                                        },
                                    }}
                                />
                            </Box>
                        ))}
                    </RadioGroup>
                    <Typography
                        sx={{
                            color: '#FF2B1F',
                            fontSize: '0.875rem',
                            minHeight: '1.25rem',
                            opacity: errors.gender ? 1 : 0,
                            visibility: errors.gender ? 'visible' : 'hidden',
                            transition: 'opacity 0.3s ease, visibility 0.3s ease',
                            mt: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                        }}
                        >
                        <Box
                            component="span"
                            sx={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: '#FF2B1F',
                            color: 'var(--color-text-inverse)',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight:'normal'
                            }}
                        >
                            !
                        </Box>
                       {errors.gender}
                        </Typography>
                </Box>
                {/* {role === 1 && ( */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", mt: 2, gap: 1, width: '100%' }}>
                <Box
                onClick={() => onPrivacyConsentChange(!privacyConsent)}
                sx={{
                  width: { xs: "36px", sm: "30px" },
                  height: { xs: "36px", sm: "30px" },
                  minWidth: { xs: "36px", sm: "30px" },
                  minHeight: { xs: "36px", sm: "30px" },
                  marginBottom: "3px",
                  cursor: "pointer",
                  border: "2px solid var(--color-border-dark)",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: privacyConsent ? "var(--color-primary)" : "transparent",
                  transition: "all 0.2s ease",
                  flexShrink: 0,
                  "&:hover": {
                    borderColor: "var(--color-border-hover)",
                    backgroundColor: privacyConsent ? "var(--color-primary)" : "transparent",
                  },
                }}
              >
                {privacyConsent && (
                  <CheckIcon
                    sx={{
                      width: { xs: "30px", sm: "24px" },
                      height: { xs: "30px", sm: "24px" },
                      color: "var(--color-text-inverse)",
                    }}
                  />
                )}
              </Box>
              <Typography variant="body2" sx={{ fontSize: "1rem", color: "var(--color-text-primary)", fontWeight: 400, textAlign: "left" }}>
                {getTranslation("Txt843")}{" "}
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    fontSize: "0.75rem",
                    color: "var(--color-text-primary)",
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": {
                      color: "var(--color-text-primary)",
                    },
                  }}
                  onClick={() => {
                    sessionStorage.setItem('privacyPolicyPrevView', 'user-info');
                    setCurrentView("privacy-policy");
                  }}
                >
                  {getTranslation("Txt844")}
                </Typography>
                .
              </Typography>
                </Box>
                {/* )} */}

                <Box sx={{ width: '100%', display: 'flex', justifyContent:  'center'}}>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isLoading || !privacyConsent}
                        sx={{
                            ...COMMON_MUI_STYLES.primaryCtaButton1,
                            width: { xs: "100%", sm: "auto" },
                            mt: 2,
                            mb: 2,
                            position: 'relative',
                            '& > *:not(.loader-overlay)': {
                                opacity: isLoading ? 0 : 1,
                                transition: 'opacity 0.2s ease-in-out',
                            },
                        }}
                        onClick={() => handleSubmit()}
                    >
                        <span>{getTranslation("Txt113")}</span>
                        {isLoading && (
                            <CircularProgress
                                size={20}
                                className="loader-overlay"
                                sx={{
                                    color: 'var(--color-text-inverse)',
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    marginTop: '-10px',
                                    marginLeft: '-10px',
                                }}
                            />
                        )}
                    </Button>
                </Box>
            </Box>
        </Layout>
    );
};

export default UserInfoPage;